import { css, cx } from "@emotion/css"

import { Link } from "react-router-dom"
import { useI18n } from "providers/I18n"
import { useRevenueParams } from "../../hooks/useRevenueParams"

import React, { ReactNode } from "react"

const separator = (
  <span style={{ color: "var(--primary-main-green)" }}>&nbsp;/&nbsp;</span>
)

const LinkLayout = ({
  children,
  isActiveLink,
}: {
  children: ReactNode
  isActiveLink: boolean
}) => {
  return (
    <>
      {separator}
      <span className={!isActiveLink ? styles.text : ""}>{children}</span>
    </>
  )
}

const CustomLink = ({
  children,
  to,
  isActiveLink,
  disabled,
}: {
  children: React.ReactNode
  to: () => {
    pathname: string
    search: string
  }
  isActiveLink: boolean
  disabled: boolean
}) => {
  return (
    <Link
      to={to}
      style={{ pointerEvents: disabled ? "none" : "all" }}
      className={cx(
        css`
          color: var(--primary-main-green);
          text-decoration: none;
        `,
        isActiveLink && styles.activeLink,
      )}
    >
      <LinkLayout isActiveLink={isActiveLink}>{children}</LinkLayout>
    </Link>
  )
}

export function BreadCrumbs({
  clientName,
  isLoading,
  userRoles,
}: {
  clientName?: string
  isLoading: boolean
  userRoles: {
    isMarketFinance: boolean
    isRegionFinance: boolean
    isGmpUser: boolean
  }
}) {
  const { t } = useI18n()
  const {
    pathname,
    search,
    clientParam,
    regionParam,
    marketParam,
    brandParam,
  } = useRevenueParams()

  const hasClient = !!clientParam && clientParam !== "All"
  const hasBrand = !!brandParam && brandParam !== "All"
  const hasMarket = !!marketParam && marketParam !== "All"
  const hasRegion = !!regionParam && regionParam !== "All"

  const onFinancialClick = React.useCallback(() => {
    const updatedSearch = new URLSearchParams(search)
    updatedSearch.set("market", "All")
    updatedSearch.set("brand", "All")
    updatedSearch.set("region", "All")
    updatedSearch.delete("client")
    return { pathname, search: updatedSearch.toString() }
  }, [pathname, search])

  const onRegionClick = React.useCallback(() => {
    const updatedSearch = new URLSearchParams(search)
    updatedSearch.set("market", "All")
    updatedSearch.set("brand", "All")
    updatedSearch.delete("client")
    return { pathname, search: updatedSearch.toString() }
  }, [pathname, search])

  const onMarketClick = React.useCallback(() => {
    const updatedSearch = new URLSearchParams(search)
    updatedSearch.set("brand", "All")
    updatedSearch.delete("client")
    return { pathname, search: updatedSearch.toString() }
  }, [pathname, search])

  const onBrandClick = React.useCallback(() => {
    const updatedSearch = new URLSearchParams(search)
    updatedSearch.delete("client")
    return { pathname, search: updatedSearch.toString() }
  }, [pathname, search])

  const infos = [
    {
      text: t("revenue.financial"),
      to: onFinancialClick,
      disabled:
        userRoles.isRegionFinance ||
        userRoles.isMarketFinance ||
        userRoles.isGmpUser,
    },
    {
      text: hasRegion ? regionParam : null,
      to: onRegionClick,
      disabled: userRoles.isMarketFinance || userRoles.isGmpUser,
    },
    {
      text: hasMarket ? marketParam : null,
      to: onMarketClick,
      disabled: userRoles.isGmpUser,
    },
    {
      text: hasBrand ? brandParam : null,
      to: onBrandClick,
      disabled: userRoles.isGmpUser,
    },
  ]

  const activeBreadCrumbs: {
    text: string | null
    to: () => {
      pathname: string
      search: string
    }
    disabled: boolean
  }[] = infos.filter((x) => !!x.text)

  return (
    <div className={cx("BodyBody2Regular", styles.root)}>
      <span>{t("revenue.dashboards")}</span>
      {activeBreadCrumbs.map((info, index) => (
        <CustomLink
          key={index}
          to={info.to}
          disabled={info.disabled}
          isActiveLink={
            index === activeBreadCrumbs.length - 1 &&
            !hasClient &&
            (!!activeBreadCrumbs || activeBreadCrumbs === 1)
          }
        >
          {info.text}
        </CustomLink>
      ))}
      {!isLoading && !!hasClient && (
        <span className={styles.activeLink}>
          {separator}
          {clientName || clientParam}
        </span>
      )}
    </div>
  )
}

const styles = {
  root: css`
    display: flex;
    opacity: 0.6;
    text-align: left;
    color: var(--primary-main-green);
  `,
  activeLink: css`
    color: var(--white);
    cursor: auto;
  `,
  text: css`
    &:hover {
      color: var(--primary-main-light-green);
    }
  `,
}
